import React, { ComponentProps } from 'react';
import { cx } from 'class-variance-authority';
import { Button as BaseUIButton } from '@base-ui/react/button';
import Typography from '@ui/Typography';
import { variants, type ButtonVariants } from './button-variants';
import LoadingIcon from '../../../LoadingIndicators/CircularLoader';

export type PartialNativeButton = Pick<
  ComponentProps<'button'>,
  'className' | 'style'
>;

/**
 * Can remove once facade ui/Button is removed
 * with legacy button prop types
 */
export type PartialBaseButton = Omit<
  ComponentProps<typeof BaseUIButton>,
  'className' | 'style'
>;

export type ButtonProps = PartialNativeButton &
  PartialBaseButton &
  ButtonVariants & {
    label?: string;
    loading?: boolean;
    fullWidth?: boolean;
  };

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(props, ref) {
    const {
      id,
      variant = 'primary',
      size,
      label,
      loading,
      children,
      className,
      disabled,
      fullWidth,
      ...rest
    } = props;

    // MUI-era props from the facade during migration; not forwarded to Base UI.
    const {
      color: _color,
      sx: _sx,
      disableElevation: _disableElevation,
      disableFocusRipple: _disableFocusRipple,
      size: _size,
      startIcon: _startIcon,
      endIcon: _endIcon,
      ...baseRest
    } = rest as typeof rest & {
      color?: unknown;
      sx?: unknown;
      disableElevation?: boolean;
      disableFocusRipple?: boolean;
      size?: unknown;
      startIcon?: React.ReactNode;
      endIcon?: React.ReactNode;
    };

    const content =
      children ??
      (label ? (
        <Typography variant="h5" component="p" bold color="inherit">
          {label}
        </Typography>
      ) : null);

    return (
      <BaseUIButton
        ref={ref}
        id={id}
        data-e2e={id}
        type="button"
        disabled={Boolean(loading || disabled)}
        aria-busy={loading || undefined}
        {...baseRest}
        className={cx(
          className,
          variants({
            variant,
            size,
            fullWidth: fullWidth ? true : undefined,
          })
        )}
      >
        {/* todo assumption about loading size
        todo replace w new loader spinner */}
        {loading ? <LoadingIcon size={16} /> : content}
      </BaseUIButton>
    );
  }
);

Button.displayName = 'Button';
